import { StyledForm } from '@Components/shared/Form.styles';
import { getAssets, getAssetsByIdStartsWith } from '@Services/assets';
import { useGlobalization } from '@Translations';
import {
	ConfigurationDataSourceType,
	SensorConfigFormShape,
} from '@Types/configurations.types';
import { FLAGS } from '@Types/flags';
import { Units } from '@Types/sensorTypes.types';
import { TSensorTypes } from '@Utils/valuemappingFormHelpers';
import { Pagination, SensorType } from '@innovyze/shared-utils';
import { Features, useIsFeatureEnabled } from '@innovyze/stylovyze';
import {
	FormControl,
	FormControlLabel,
	FormHelperText,
	Grid,
	InputAdornment,
	InputLabel,
	MenuItem,
	Radio,
	RadioGroup,
	Select,
	SelectChangeEvent,
	Tooltip,
	TooltipProps,
	styled,
	tooltipClasses,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import { AxiosResponse } from 'axios';
import React, { ChangeEvent, useMemo } from 'react';
import { SystemConnection } from '../../types/systemConnections.types';
import { ConnectionStringPeekField } from './ConnectionStringPeekField';
import { Input } from './SensorConfigurationForm.styles';
import { isVirtualSensor } from '@Utils';
import { SensorTroubleshooting } from '@Types/peek';
import { SensorFormChanges, useSensorTroubleshootMessages } from '@Hooks';
import { ServerType } from '@Types/CreateNewSystemConnectionRequest';

type Optional<T, K extends keyof T> = Pick<Partial<T>, K> & Omit<T, K>;

interface AssetResponse {
	assetId: string;
	assetType: string;
	tankCurveId?: string;
}

const CustomTooltip = styled(({ className, ...props }: TooltipProps) => (
	<Tooltip {...props} classes={{ popper: className }} />
))({
	[`& .${tooltipClasses.tooltip}`]: {
		maxWidth: 'none',
	},
	[`& .${tooltipClasses.arrow}`]: {
		transform: 'translate(16px, 0) !important',
	},
});

export interface SensorConfigurationFormProps {
	updateFormData: (
		formData: React.SetStateAction<SensorConfigFormShape>,
	) => void;
	formData: Optional<SensorConfigFormShape, 'tankCurveId'>;
	formErrors: SensorConfigFormShape;
	sensorTypes: SensorType[];
	systemConnections: Array<DataSource>;
	coordinateChange: (p: string) => void;
	mode: SensorFormMode;
	units: Units;
	sensorTroubleshooting: Partial<SensorTroubleshooting>;
	isCurrentlyTesting: boolean;
	sensorFormResampleChanges?: Partial<SensorFormChanges>;
}

export enum SensorFormMode {
	EDIT = 'edit',
	ADD = 'add',
	VIEW = 'view',
}

export interface DataSource {
	connectionName: string;
	SystemConnectionId: string;
	serverType: ServerType;
}

export interface DataSourceType {
	dataSourceTypeName: string;
	dataSourceTypeId: string;
}

export interface AssetOption {
	id: string;
	type: string;
	tankCurveId?: string;
}

export const SensorConfigurationsForm = (
	props: SensorConfigurationFormProps,
) => {
	const { t } = useGlobalization();
	const [assetOptionsListOpen, setAssetOptionsListOpen] =
		React.useState(false);
	const [tankCurveOptionsListOpen, setTankCurveOptionsListOpen] =
		React.useState(false);
	const [assetIdToSearch, setAssetIdToSearch] = React.useState('');
	const [tankCurveIdToSearch, setTankCurveIdToSearch] = React.useState('');
	const [selectedAsset, setSelectedAsset] =
		React.useState<AssetOption | null>();
	const [selectedTankCurve, setSelectedTankCurve] =
		React.useState<AssetOption | null>();
	const [assetOptions, setAssetOptions] = React.useState<AssetOption[]>();
	const [tankCurveOptions, setTankCurveOptions] =
		React.useState<AssetOption[]>();
	const [isFetching, setIsFetching] = React.useState(false);
	const [isFetchingTankCurve, setIsFetchingTankCurve] = React.useState(false);
	const [newAssetIdToCreate, setNewAssetIdToCreate] = React.useState<
		string | undefined
	>(undefined);

	const tblt = useSensorTroubleshootMessages(
		props.sensorTroubleshooting,
		props.formData.dataSourceId,
		props.systemConnections as never,
	);
	const isViewMode = props.mode === SensorFormMode.VIEW;
	const tagValueFieldColor = useMemo(
		() =>
			tblt.isTimeFramed && !!tblt.messages.tagValue
				? 'warning'
				: 'primary',
		[tblt.messages, tblt.messages.tagValue, props.systemConnections],
	);

	const debounceTimeInMs = 500;

	const isVirtual = isVirtualSensor(props.formData.dataSourceType);

	const timeFieldEnabled =
		useIsFeatureEnabled(
			FLAGS.SENSOR_CONFIG_RENDER_TIME_FIELD as keyof Features,
		) ?? false;

	const isOpcuaEnabled = useIsFeatureEnabled(
		FLAGS.INFO_360_ADMIN_OPCUA as keyof Features,
	);
	const isOledbEnabled = useIsFeatureEnabled(
		FLAGS.INFO_360_ADMIN_OLEDB as keyof Features,
	);

	const isVirtualSensorsEnabled = useIsFeatureEnabled(
		FLAGS.ENABLE_VIRTUAL_SENSORS_ON_SENSOR_CONFIGURATION as keyof Features,
	);

	const isCylindricalTankEnabled = Boolean(
		useIsFeatureEnabled(FLAGS.CYLINDRICAL_TANK as keyof Features),
	);

	const loadingAsset =
		assetOptionsListOpen && isFetching && assetIdToSearch !== '';

	const loadingTankCurve =
		tankCurveOptionsListOpen &&
		isFetchingTankCurve &&
		tankCurveIdToSearch !== '';

	const textChanged = (
		event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
	) => {
		const target = event?.target as HTMLInputElement;
		if (target && target.name) {
			props.updateFormData((formData: SensorConfigFormShape) => ({
				...formData,
				[target?.name]: target?.value,
				...(target?.name === 'isPhysical'
					? target?.value === 'true'
						? {
								dataSourceType: 'ODBC',
								dataSourceId: '',
								dataSourceName: '',
						  }
						: {
								dataSourceType: 'FORM',
								dataSourceId: '',
								dataSourceName: '',
						  }
					: {}),
			}));
			if (target?.name === 'location') {
				props.coordinateChange(target?.value);
			}
		}
	};

	const inputChanged = (event: React.SyntheticEvent<Element, Event>) => {
		textChanged(event as ChangeEvent<HTMLInputElement>);
	};
	const selectChanged = (event: SelectChangeEvent) => {
		textChanged(event as ChangeEvent<HTMLInputElement>);
	};

	const onChangeAliasAutocompleteHandler = (
		event: React.ChangeEvent<unknown>,
	) => {
		const target = event?.target as HTMLInputElement;
		if (target) {
			const selectedIdx = parseInt(target?.value);
			const newValue =
				[props.formData?.sensorId || ''][selectedIdx] || '';
			props.updateFormData((formData: SensorConfigFormShape) => ({
				...formData,
				sensorAlias: newValue,
			}));
		}
	};

	const handleSelectChange = (event: SelectChangeEvent<string>) => {
		const target = event?.target as HTMLInputElement;
		if (target?.name) {
			props.updateFormData((formData: SensorConfigFormShape) => ({
				...formData,
				[target?.name]: target?.value,
				...(target?.name === 'sensorType' &&
				props.formData?.isPhysical === 'false'
					? {
							unit: '',
					  }
					: {}),
			}));
		}
	};

	const handleDataSourceTypeSelectChange = (
		event: SelectChangeEvent<string>,
	) => {
		const target = event?.target as HTMLInputElement;
		if (target?.name) {
			const value = target?.value;
			props.updateFormData((formData: SensorConfigFormShape) => ({
				...formData,
				[target?.name]: value,
				['isPhysical']:
					value === ConfigurationDataSourceType.Form ||
					value === ConfigurationDataSourceType.BULK_UPLOAD
						? 'false'
						: 'true',
				['dataSourceName']: '',
				['dataSourceId']: '',
			}));
		}
	};

	const fetchAssets = async (id?: string): Promise<AssetResponse[]> => {
		let assetResponses: AssetResponse[];
		if (id) {
			const response: AxiosResponse<AssetResponse[]> =
				await getAssetsByIdStartsWith(id);
			assetResponses = response.data;
		} else {
			const response: AxiosResponse<{
				assets: AssetResponse[];
				pagination: Pagination;
			}> = await getAssets();
			assetResponses = response.data.assets;
		}
		return assetResponses || [];
	};

	const buildAssetOptions = (assets: AssetResponse[]): AssetOption[] => {
		return assets.map<AssetOption>(asset => ({
			id: asset.assetId,
			type: asset.assetType,
			tankCurveId: asset.tankCurveId || undefined,
		}));
	};

	React.useEffect(() => {
		const fetchAssetOptions = async () => {
			setIsFetching(true);
			const assets = await fetchAssets(assetIdToSearch);
			setAssetOptions(buildAssetOptions(assets));
			setIsFetching(false);
		};
		const timeoutId = setTimeout(
			() => fetchAssetOptions(),
			debounceTimeInMs,
		);
		return () => clearTimeout(timeoutId);
	}, [assetIdToSearch]);

	const buildTankCurveOptions = (assets: AssetResponse[]): AssetOption[] =>
		assets
			.filter(el => el.assetType === 'tank_curve')
			.map(el => ({
				id: el.assetId,
				type: el.assetType,
			}));

	React.useEffect(() => {
		const searchTankCurves = async () => {
			setIsFetchingTankCurve(true);
			const assets = await fetchAssets(tankCurveIdToSearch);
			setTankCurveOptions(buildTankCurveOptions(assets));
			setIsFetchingTankCurve(false);
		};
		const timeoutId = setTimeout(
			() => searchTankCurves(),
			debounceTimeInMs,
		);
		return () => clearTimeout(timeoutId);
	}, [tankCurveIdToSearch]);

	const isSensorTypeTankLevel = props.formData?.sensorType === 'Tank Level';

	React.useEffect(() => {
		if (!isSensorTypeTankLevel) {
			delete props.formData.tankCurveId;
		}
	}, [props.formData?.sensorType]);

	const onBlurAsset = () => {
		if (assetOptions) {
			const validSelectedOption = assetOptions.find(
				asset => asset.id === props.formData.assetId,
			);
			if (!validSelectedOption) {
				setNewAssetIdToCreate(props.formData.assetId);
			}
		}
	};

	const onBlurTankCurve = () => {
		if (tankCurveOptions) {
			const validSelectedOption = tankCurveOptions.find(
				asset => asset.id === props.formData.tankCurveId,
			);
			if (!validSelectedOption) {
				props.formData.tankCurveId = '';
				setTankCurveIdToSearch('');
			}
		} else {
			props.formData.tankCurveId = '';
			setTankCurveIdToSearch('');
		}
	};

	const onAssetTextChanged = async (
		event:
			| KeyboardEvent
			| React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
	) => {
		const target = event?.target as HTMLInputElement;
		const searchById = target?.value;
		props.formData.assetId = searchById;
		setAssetIdToSearch(searchById);
	};

	const onTankCurveTextChanged = async (
		event:
			| KeyboardEvent
			| React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
	) => {
		const target = event?.target as HTMLInputElement;
		const value = target?.value;
		props.formData.tankCurveId = value;
		setTankCurveIdToSearch(value);
	};

	const renderMenuItems = (items: string[]) => {
		const translationValues = TSensorTypes(t);
		return items.map((el: string) => (
			<MenuItem key={el} value={el}>
				{translationValues[el] ? translationValues[el] : el}
			</MenuItem>
		));
	};

	let measureUnits: string[] = [];
	if (
		props.formData?.sensorType in SensorType ||
		Object.values(SensorType).includes(
			props.formData?.sensorType as SensorType,
		)
	) {
		const sensorType = props.formData?.sensorType as SensorType;
		measureUnits = sensorType ? props.units[sensorType as never] : [];
	}

	const buildAssetOptionLabel = (option: { type: string; id: string }) => {
		if (option.type !== '') {
			return option.type + ' - ' + option.id;
		} else {
			return option.id ? option.id : '';
		}
	};

	const dataSourceTypes = React.useMemo((): Array<DataSourceType> => {
		const response = new Array<DataSourceType>();
		const customAnalytics = {
			dataSourceTypeId: ConfigurationDataSourceType.CUSTOM_ANALYTICS,
			dataSourceTypeName: t('Custom Analytics'),
		};
		const mb = {
			dataSourceTypeId: ConfigurationDataSourceType.MASS_BALANCE,
			dataSourceTypeName: t('Mass Balance'),
		};
		const ili = {
			dataSourceTypeId: ConfigurationDataSourceType.ILI,
			dataSourceTypeName: t('ILI'),
		};
		const FORM = {
			dataSourceTypeId: ConfigurationDataSourceType.Form,
			dataSourceTypeName: t('Form'),
		};
		const ODBC = {
			dataSourceTypeId: ConfigurationDataSourceType.ODBC,
			dataSourceTypeName: t('ODBC'),
		};
		const OLEDB = {
			dataSourceTypeId: ConfigurationDataSourceType.OLEDB,
			dataSourceTypeName: t('OLEDB'),
		};
		const OPC = {
			dataSourceTypeId: ConfigurationDataSourceType.OPCUA,
			dataSourceTypeName: t('OPC-UA'),
		};
		const BULK_UPLOAD = {
			dataSourceTypeId: ConfigurationDataSourceType.BULK_UPLOAD,
			dataSourceTypeName: t('Bulk Upload'),
		};
		if (props.mode === SensorFormMode.ADD) {
			if (props.formData?.isPhysical === 'true') {
				response.push(ODBC);
				if (isOledbEnabled) {
					response.push(OLEDB);
				}
				if (isOpcuaEnabled) {
					response.push(OPC);
				}
			} else {
				response.push(FORM);
			}
		} else {
			if (isVirtual) {
				switch (props.formData.dataSourceType) {
					case ConfigurationDataSourceType.CUSTOM_ANALYTICS:
						response.push(customAnalytics);
						break;
					case ConfigurationDataSourceType.MASS_BALANCE:
						response.push(mb);
						break;
					case ConfigurationDataSourceType.ILI:
						response.push(ili);
						break;
				}
			} else {
				response.push(ODBC);
				if (isOledbEnabled) {
					response.push(OLEDB);
				}
				if (isOpcuaEnabled) {
					response.push(OPC);
				}
				response.push(FORM);
			}
			response.push(BULK_UPLOAD);
		}
		return response;
	}, [
		isVirtual,
		props.formData.dataSourceType,
		props.formData.isPhysical,
		props.mode,
	]);

	const filterSystemConnections = (
		dataSourceType: string,
	): Array<DataSource> => {
		const systemConnections =
			props.systemConnections as Array<SystemConnection>;
		const filteredSystemConnections = systemConnections.filter(
			systemConnection => {
				if (systemConnection.connectionType === dataSourceType) {
					return systemConnections;
				}
			},
		);
		return filteredSystemConnections as Array<DataSource>;
	};

	const renderConnectionField = () => {
		return (
			<Grid item xs={12}>
				<ConnectionStringPeekField
					formData={props.formData as SensorConfigFormShape}
				/>
			</Grid>
		);
	};

	const handleAssetChange = async (
		_event: React.SyntheticEvent,
		value: string | AssetOption | null,
	) => {
		const assetOption = value as AssetOption;
		if (assetOption) {
			setSelectedAsset(assetOption);
			props.formData.assetId = assetOption.id;
			const tankCurveId = assetOption.tankCurveId || '';
			props.updateFormData((formData: SensorConfigFormShape) => ({
				...formData,
				tankCurveId,
			}));
			if (isSensorTypeTankLevel) {
				setTankCurveIdToSearch(tankCurveId);
			}
		} else {
			props.formData.assetId = '';
			setSelectedAsset(null);
			setAssetIdToSearch('');
		}
	};
	const AssetAutoComplete = (
		<Autocomplete
			selectOnFocus
			clearOnBlur
			handleHomeEndKeys
			id="assetId"
			disabled={isViewMode}
			freeSolo={false}
			value={{ id: props.formData.assetId, type: '' }}
			style={{ width: '100%', marginTop: 15 }}
			open={assetOptionsListOpen}
			noOptionsText={
				assetIdToSearch === ''
					? t('Start typing asset ID..')
					: t('No result (create new)')
			}
			onOpen={() => {
				setAssetOptionsListOpen(true);
			}}
			onClose={() => {
				setAssetOptionsListOpen(false);
			}}
			onBlur={onBlurAsset}
			isOptionEqualToValue={(option, value) => {
				return option.id === value.id;
			}}
			getOptionLabel={buildAssetOptionLabel}
			options={assetOptions ?? []}
			onChange={handleAssetChange}
			loading={loadingAsset}
			renderInput={params => (
				<TextField
					{...params}
					label={t('Asset')}
					variant="outlined"
					onChange={onAssetTextChanged}
					value={selectedAsset}
					helperText={
						newAssetIdToCreate ? t('Creating new asset') : undefined
					}
					InputProps={{
						...params.InputProps,
						endAdornment: (
							<React.Fragment>
								{loadingAsset ? (
									<CircularProgress
										color="inherit"
										size={20}
									/>
								) : null}
								{params.InputProps.endAdornment}
							</React.Fragment>
						),
					}}
				/>
			)}
		/>
	);

	const CylindricalInputs = (
		<>
			<Grid item xs={6}>
				<Input
					name={'tankOffset'}
					value={props.formData?.tankOffset ?? ''}
					color={'primary'}
					fullWidth
					margin={'normal'}
					label={t('Offset')}
					variant="outlined"
					onChange={textChanged}
					error={!!props.formErrors?.tankOffset}
					helperText={props.formErrors?.tankOffset}
					InputProps={{
						endAdornment: (
							<InputAdornment position="end">
								{props.formData?.unit ?? ''}
							</InputAdornment>
						),
					}}
					disabled={isViewMode}
				/>
			</Grid>
			<Grid item xs={6}>
				<Input
					name={'tankDiameter'}
					value={props.formData?.tankDiameter ?? ''}
					color={'primary'}
					fullWidth
					margin={'normal'}
					label={t('Diameter')}
					variant="outlined"
					onChange={textChanged}
					error={!!props.formErrors?.tankDiameter}
					helperText={props.formErrors?.tankDiameter}
					InputProps={{
						endAdornment: (
							<InputAdornment position="end">
								{props.formData?.unit ?? ''}
							</InputAdornment>
						),
					}}
					disabled={isViewMode}
				/>
			</Grid>
		</>
	);

	const TankCurveAutoComplete = (
		<Autocomplete
			id="tankCurveId"
			freeSolo={false}
			disabled={!isSensorTypeTankLevel || isViewMode}
			value={{
				id: isSensorTypeTankLevel
					? props.formData.tankCurveId ?? ''
					: '', // hide the value when disabled
				type: '',
			}}
			style={{ width: '100%', marginTop: 15 }}
			open={tankCurveOptionsListOpen}
			noOptionsText={
				tankCurveIdToSearch === ''
					? t('Start typing tank curve ID..')
					: t('No asset found')
			}
			onOpen={() => {
				setTankCurveOptionsListOpen(true);
			}}
			onClose={() => {
				setTankCurveOptionsListOpen(false);
			}}
			onBlur={onBlurTankCurve}
			isOptionEqualToValue={(option, value) => {
				return option.id === value.id;
			}}
			getOptionLabel={buildAssetOptionLabel}
			options={tankCurveOptions ?? []}
			onChange={(_event, value: string | AssetOption | null) => {
				const assetOption = value as AssetOption;
				if (assetOption) {
					setSelectedTankCurve(assetOption);
					props.formData.tankCurveId = assetOption.id;
				} else {
					props.formData.tankCurveId = '';
					setSelectedTankCurve(null);
					setTankCurveIdToSearch('');
				}
			}}
			loading={loadingTankCurve}
			renderInput={params => (
				<TextField
					{...params}
					label={t('Tank Curve')}
					variant="outlined"
					onChange={onTankCurveTextChanged}
					value={selectedTankCurve}
					InputProps={{
						...params.InputProps,
						endAdornment: (
							<React.Fragment>
								{loadingTankCurve ? (
									<CircularProgress
										color="inherit"
										size={20}
									/>
								) : null}
								{params.InputProps.endAdornment}
							</React.Fragment>
						),
					}}
				/>
			)}
		/>
	);

	const NonCylindricalInputs = (
		<>
			<Grid item xs={6}></Grid>
			<Grid item xs={6}>
				{TankCurveAutoComplete}
			</Grid>
		</>
	);
	const renderCommonFields = () => {
		return (
			<>
				<Grid item xs={6}>
					<Input
						required
						name={'sensorId'}
						value={props.formData?.sensorId || ''}
						color={'primary'}
						fullWidth
						margin={'normal'}
						label={t('Sensor ID')}
						variant="outlined"
						onChange={textChanged}
						error={!!props.formErrors?.sensorId}
						helperText={props.formErrors?.sensorId}
						disabled={props.mode !== SensorFormMode.ADD}
					/>
				</Grid>
				<Grid item xs={6}>
					<Autocomplete
						id="sensorAlias-autocomplete"
						value={props.formData?.sensorAlias || ''}
						onChange={onChangeAliasAutocompleteHandler}
						color={'primary'}
						options={
							props.formData?.sensorId
								? [props.formData?.sensorId]
								: []
						}
						disabled={isVirtual || isViewMode}
						fullWidth
						renderInput={params => (
							<Input
								{...params}
								name={'sensorAlias'}
								label={t('Sensor Alias')}
								variant="outlined"
								margin={'normal'}
								error={!!props.formErrors?.sensorAlias}
								helperText={props.formErrors?.sensorAlias}
								required
								disabled={isVirtual || isViewMode}
							/>
						)}
						inputValue={`${props.formData?.sensorAlias || ''}`}
						onInputChange={inputChanged}
						noOptionsText={t('No options')}
					/>
				</Grid>
				<Grid item xs={6}>
					<FormControl variant="outlined" margin="normal" fullWidth>
						<InputLabel
							error={!!props.formErrors?.sensorType}
							required>
							{t('Sensor Type')}
						</InputLabel>
						<Select
							data-cy="select-sensor-type"
							name="sensorType"
							value={props.formData?.sensorType ?? ''}
							onChange={handleSelectChange}
							error={!!props.formErrors?.sensorType}
							label={t('Sensor Type .')}
							disabled={isVirtual || isViewMode}>
							{renderMenuItems(props.sensorTypes)}
						</Select>
						<FormHelperText
							error={!!Object.keys(props.formErrors).length}>
							{props.formErrors?.sensorType}
						</FormHelperText>
					</FormControl>
				</Grid>
				<Grid item xs={6}>
					<FormControl variant="outlined" margin="normal" fullWidth>
						<InputLabel error={!!props.formErrors?.unit}>
							{t(
								`Source Unit ${
									props.formData?.dataSourceType ===
									ConfigurationDataSourceType.Form
										? '*'
										: ''
								}`,
							)}
						</InputLabel>
						<Select
							data-cy="select-source-unit"
							disabled={
								!measureUnits ||
								!measureUnits.length ||
								isVirtual ||
								isViewMode
							}
							name={'unit'}
							value={props.formData?.unit || ''}
							fullWidth
							required={props.formData?.isPhysical === 'false'}
							color={'primary'}
							label={t('Source Unit')}
							variant="outlined"
							onChange={selectChanged}
							error={!!props.formErrors?.unit}>
							{renderMenuItems(measureUnits || [])}
						</Select>
						<FormHelperText
							error={!!Object.keys(props.formErrors).length}>
							{props.formErrors?.unit}
						</FormHelperText>
					</FormControl>
				</Grid>
				<Grid item xs={6}>
					{AssetAutoComplete}
				</Grid>
				{(props.formData?.isPhysical !== 'false' ||
					props.formData?.sensorType === 'Tank Level') && (
					<Grid item xs={6}>
						{!isCylindricalTankEnabled && TankCurveAutoComplete}
						{isCylindricalTankEnabled &&
							props.formData?.sensorType === 'Tank Level' && (
								<FormControl
									variant="outlined"
									margin="normal"
									fullWidth>
									<InputLabel
										required
										error={!!props.formErrors?.tankType}>
										{t('Tank Type')}
									</InputLabel>
									<Select
										data-cy="select-tank-type"
										name="tankType"
										value={
											props.formData?.tankType ??
											'cylindrical'
										}
										onChange={handleSelectChange}
										error={!!props.formErrors?.tankType}
										label={t('Tank Type')}
										disabled={isViewMode}>
										<MenuItem
											key={'cylindrical'}
											value={'cylindrical'}>
											{t('Cylindrical')}
										</MenuItem>
										<MenuItem
											key={'non-cylindrical'}
											value={'non-cylindrical'}>
											{t('Non-cylindrical')}
										</MenuItem>
									</Select>
								</FormControl>
							)}
					</Grid>
				)}

				{isCylindricalTankEnabled &&
					props.formData?.sensorType === 'Tank Level' &&
					props.formData?.tankType !== 'non-cylindrical' &&
					CylindricalInputs}
				{isCylindricalTankEnabled &&
					props.formData?.sensorType === 'Tank Level' &&
					props.formData?.tankType === 'non-cylindrical' &&
					NonCylindricalInputs}
				{props.formData?.isPhysical !== 'false' && (
					<Grid item xs={6}>
						<Input
							name={'collectionInterval'}
							value={props.formData?.collectionInterval || ''}
							color={'primary'}
							fullWidth
							margin={'normal'}
							label={t('Sensor Interval (seconds)')}
							variant="outlined"
							onChange={textChanged}
							error={
								!!props.formErrors?.collectionInterval ||
								!!props?.sensorFormResampleChanges
									?.collectionInterval
							}
							helperText={
								props.formErrors?.collectionInterval ||
								props?.sensorFormResampleChanges
									?.collectionInterval
							}
							disabled={isViewMode}
						/>
					</Grid>
				)}

				<Grid item xs={6}>
					<Input
						name={'location'}
						value={props.formData?.location ?? ''}
						color={'primary'}
						fullWidth
						margin={'normal'}
						label={t('Location (lat,lng)')}
						variant="outlined"
						onChange={textChanged}
						error={!!props.formErrors?.location}
						helperText={props.formErrors?.location}
						disabled={isViewMode}
					/>
				</Grid>
				<Grid item xs={6}>
					<FormControl variant="outlined" margin="normal" fullWidth>
						<InputLabel
							color="primary"
							error={
								!!props.formErrors?.dataSourceType ||
								!!props?.sensorFormResampleChanges
									?.dataSourceType
							}
							disabled={props.isCurrentlyTesting}>
							{t('Data Source Type')}
						</InputLabel>
						<Select
							name="dataSourceType"
							value={props.formData?.dataSourceType || 'ODBC'}
							onChange={handleDataSourceTypeSelectChange}
							error={
								!!props.formErrors?.dataSourceType ||
								!!props?.sensorFormResampleChanges
									?.dataSourceType
							}
							disabled={
								props.isCurrentlyTesting ||
								isVirtual ||
								isViewMode
							}
							label={t('Data Source Type')}>
							{dataSourceTypes.map(
								({ dataSourceTypeName, dataSourceTypeId }) => (
									<MenuItem
										key={dataSourceTypeId}
										value={dataSourceTypeId}>
										{dataSourceTypeName}
									</MenuItem>
								),
							)}
						</Select>
						{(!!props.formErrors?.dataSourceType ||
							!!props?.sensorFormResampleChanges
								?.dataSourceType) && (
							<FormHelperText error={true}>
								{props.formErrors?.dataSourceType ||
									!!props?.sensorFormResampleChanges
										?.dataSourceType}
							</FormHelperText>
						)}
					</FormControl>
				</Grid>
				{props.formData?.isPhysical !== 'false' && (
					<Grid item xs={6}>
						<FormControl
							variant="outlined"
							margin="normal"
							fullWidth>
							<InputLabel
								disabled={props.isCurrentlyTesting}
								error={
									!!props.formErrors?.dataSourceId ||
									!!tblt?.messages?.dataSource ||
									!!props?.sensorFormResampleChanges
										?.dataSourceId
								}>
								{t('Data Source')}
							</InputLabel>
							<Select
								name="dataSourceId"
								value={props.formData?.dataSourceId ?? ''}
								onChange={handleSelectChange}
								error={
									!!props.formErrors?.dataSourceId ||
									!!tblt?.messages?.dataSource ||
									!!props?.sensorFormResampleChanges
										?.dataSourceId
								}
								disabled={
									props.isCurrentlyTesting || isViewMode
								}
								label={t('Data Source')}>
								<MenuItem key={'none'} value={''}>
									{t('Select')}
								</MenuItem>
								{filterSystemConnections(
									props.formData?.dataSourceType ?? 'ODBC',
								).map(
									({
										connectionName,
										SystemConnectionId,
									}) => (
										<MenuItem
											key={SystemConnectionId}
											value={SystemConnectionId}>
											{connectionName}
										</MenuItem>
									),
								)}
							</Select>
							{(!!props.formErrors?.dataSourceId ||
								!!tblt?.messages?.dataSource ||
								!!props?.sensorFormResampleChanges
									?.dataSourceId) && (
								<FormHelperText error={true}>
									{props.formErrors?.dataSourceId ||
										tblt?.messages?.dataSource ||
										props?.sensorFormResampleChanges
											?.dataSourceId}
								</FormHelperText>
							)}
						</FormControl>
					</Grid>
				)}
			</>
		);
	};

	const renderODBCForm = () => {
		return (
			<>
				{renderCommonFields()}
				{renderConnectionField()}
				<Grid item xs={6}>
					<Input
						name={'sensorTable'}
						value={props.formData?.sensorTable || ''}
						color={'primary'}
						fullWidth
						margin={'normal'}
						label={t('Sensor Table')}
						variant="outlined"
						onChange={textChanged}
						error={
							!!props.formErrors?.sensorTable ||
							!!tblt?.messages?.tableName ||
							!!props?.sensorFormResampleChanges?.sensorTable
						}
						helperText={
							props.formErrors?.sensorTable ||
							tblt?.messages?.tableName ||
							props?.sensorFormResampleChanges?.sensorTable
						}
						disabled={props.isCurrentlyTesting || isViewMode}
					/>
				</Grid>
				<Grid item xs={6}>
					<Input
						name={'sensorIdField'}
						value={props.formData?.sensorIdField ?? ''}
						color={'primary'}
						fullWidth
						margin={'normal'}
						label={t('Sensor ID Field')}
						variant="outlined"
						onChange={textChanged}
						error={
							!!props.formErrors?.sensorIdField ||
							!!tblt?.messages?.idField ||
							!!props?.sensorFormResampleChanges?.sensorIdField
						}
						helperText={
							props.formErrors?.sensorIdField ||
							tblt?.messages?.idField ||
							props?.sensorFormResampleChanges?.sensorIdField
						}
						disabled={props.isCurrentlyTesting || isViewMode}
					/>
				</Grid>
				<Grid item xs={6}>
					<TextField
						name={'tagValue'}
						required={false}
						value={props.formData?.tagValue ?? ''}
						color={tagValueFieldColor}
						focused={tagValueFieldColor === 'warning'}
						fullWidth
						margin={'normal'}
						label={t('Tag Value')}
						variant="outlined"
						onChange={textChanged}
						error={
							(!!props.formErrors?.tagValue ||
								!!tblt?.messages?.tagValue ||
								!!props?.sensorFormResampleChanges?.tagValue) &&
							tagValueFieldColor !== 'warning'
						}
						helperText={
							props.formErrors?.tagValue ||
							tblt?.messages?.tagValue ||
							props?.sensorFormResampleChanges?.tagValue
						}
						disabled={props.isCurrentlyTesting || isViewMode}
					/>
				</Grid>
				<Grid item xs={6}>
					<Input
						name={'sensorDateField'}
						value={props.formData?.sensorDateField || ''}
						color={'primary'}
						fullWidth
						margin={'normal'}
						label={t('Date Field')}
						variant="outlined"
						onChange={textChanged}
						error={
							!!props.formErrors?.sensorDateField ||
							!!tblt?.messages?.dateField ||
							!!props?.sensorFormResampleChanges?.sensorDateField
						}
						helperText={
							props.formErrors?.sensorDateField ||
							tblt?.messages?.dateField ||
							props?.sensorFormResampleChanges?.sensorDateField
						}
						disabled={props.isCurrentlyTesting || isViewMode}
					/>
				</Grid>
				{timeFieldEnabled && (
					<Grid item xs={6}>
						<Input
							name={'sensorTimeField'}
							value={props.formData?.sensorTimeField || ''}
							color={'primary'}
							fullWidth
							margin={'normal'}
							label={t('Time Field')}
							variant="outlined"
							onChange={textChanged}
							error={!!props.formErrors?.sensorTimeField}
							helperText={props.formErrors?.sensorTimeField}
							disabled={isViewMode}
						/>
					</Grid>
				)}
				<Grid item xs={6}>
					<Input
						name={'sensorChannelField'}
						value={props.formData?.sensorChannelField || ''}
						color={'primary'}
						fullWidth
						margin={'normal'}
						label={t('Channel Field')}
						variant="outlined"
						onChange={textChanged}
						error={
							!!props.formErrors?.sensorChannelField ||
							!!tblt?.messages?.channelField ||
							!!props?.sensorFormResampleChanges
								?.sensorChannelField
						}
						helperText={
							props.formErrors?.sensorChannelField ||
							tblt?.messages?.channelField ||
							props?.sensorFormResampleChanges?.sensorChannelField
						}
						disabled={props.isCurrentlyTesting || isViewMode}
					/>
				</Grid>
			</>
		);
	};

	const renderOPCUAForm = () => {
		return (
			<>
				{renderCommonFields()}
				<Grid item xs={6}>
					<Input
						name={'tagValue'}
						required={true}
						value={props.formData?.tagValue ?? ''}
						color={'primary'}
						fullWidth
						margin={'normal'}
						label={t('Node ID')}
						variant="outlined"
						onChange={textChanged}
						error={
							!!props.formErrors?.tagValue ||
							!!props?.sensorFormResampleChanges?.tagValue
						}
						helperText={
							props.formErrors?.tagValue ||
							props?.sensorFormResampleChanges?.tagValue
						}
						disabled={isViewMode}
					/>
				</Grid>
			</>
		);
	};

	const renderVirtualForm = () => {
		return renderCommonFields();
	};

	const renderCorrectForm = () => {
		switch (props.formData?.dataSourceType) {
			case ConfigurationDataSourceType.ODBC:
				return renderODBCForm();
			case ConfigurationDataSourceType.OPCUA:
				return renderOPCUAForm();
			case ConfigurationDataSourceType.CUSTOM_ANALYTICS:
			case ConfigurationDataSourceType.MASS_BALANCE:
			case ConfigurationDataSourceType.ILI:
			case ConfigurationDataSourceType.Form:
			case ConfigurationDataSourceType.BULK_UPLOAD:
				return renderVirtualForm();
			default:
				return renderODBCForm();
		}
	};

	return (
		<StyledForm>
			<Grid container spacing={3}>
				{isVirtualSensorsEnabled && (
					<Grid item xs={12}>
						<RadioGroup
							row
							name="isPhysical"
							value={props.formData?.isPhysical ?? 'true'}
							onChange={textChanged}>
							<CustomTooltip
								title={t(
									'Physical sensors are for live sensor data retrieved via a system connection',
								)}
								arrow
								placement="top-start">
								<FormControlLabel
									value="true"
									disabled={props.mode !== SensorFormMode.ADD}
									control={<Radio />}
									label={t('Physical Sensor')}
								/>
							</CustomTooltip>
							<CustomTooltip
								title={t(
									'Virtual sensors are for time series data generated within Info360 or uploaded manually',
								)}
								arrow
								placement="top-start">
								<FormControlLabel
									value="false"
									disabled={props.mode !== SensorFormMode.ADD}
									control={<Radio />}
									label={t('Virtual Sensor')}
								/>
							</CustomTooltip>
						</RadioGroup>
					</Grid>
				)}
				{renderCorrectForm()}
			</Grid>
		</StyledForm>
	);
};
